import React, { useEffect, useState } from 'react';
import { getBodyParts, getExercises, createExercise, updateExercise, deleteExercise } from '../api';

const ExerciseManagePage = () => {
  const [bodyParts, setBodyParts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selectedBodyPart, setSelectedBodyPart] = useState('');
  const [loading, setLoading] = useState(true);

  // 追加フォーム
  const [newName, setNewName] = useState('');
  const [newOrder, setNewOrder] = useState('');

  // 編集
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [editOrder, setEditOrder] = useState('');

  const [message, setMessage] = useState({ text: '', type: '' });

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  useEffect(() => {
    getBodyParts()
      .then((res) => {
        const parts = res.data.sort((a, b) => a.displayOrder - b.displayOrder);
        setBodyParts(parts);
        if (parts.length > 0) setSelectedBodyPart(parts[0].name);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedBodyPart) return;
    getExercises().then((res) => {
      const filtered = res.data
        .filter((e) => e.parentName === selectedBodyPart)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setExercises(filtered);
    });
  }, [selectedBodyPart]);

  const handleCreate = async () => {
    if (!newName.trim()) return showMessage('種目名を入力してください', 'error');
    if (!newOrder || isNaN(newOrder)) return showMessage('表示順を数値で入力してください', 'error');

    try {
      await createExercise({
        name: newName.trim(),
        parentName: selectedBodyPart,
        displayOrder: Number(newOrder),
      });
      setNewName('');
      setNewOrder('');
      const res = await getExercises();
      const filtered = res.data
        .filter((e) => e.parentName === selectedBodyPart)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setExercises(filtered);
      showMessage(`「${newName.trim()}」を追加しました`);
    } catch (err) {
      const msg = err.response?.data?.message || '追加に失敗しました';
      showMessage(msg, 'error');
    }
  };

  const startEdit = (exercise) => {
    setEditTarget(exercise.name);
    setEditName(exercise.name);
    setEditOrder(String(exercise.displayOrder));
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setEditName('');
    setEditOrder('');
  };

  const handleUpdate = async (oldName) => {
    if (!editName.trim()) return showMessage('種目名を入力してください', 'error');
    if (!editOrder || isNaN(editOrder)) return showMessage('表示順を数値で入力してください', 'error');

    try {
      await updateExercise({
        oldName,
        newName: editName.trim(),
        parentName: selectedBodyPart,
        displayOrder: Number(editOrder),
      });
      cancelEdit();
      const res = await getExercises();
      const filtered = res.data
        .filter((e) => e.parentName === selectedBodyPart)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setExercises(filtered);
      showMessage(`「${editName.trim()}」に更新しました`);
    } catch (err) {
      showMessage('更新に失敗しました', 'error');
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`「${name}」を削除しますか？\n※過去の記録データは残ります`)) return;
    try {
      await deleteExercise(name);
      setExercises(exercises.filter((e) => e.name !== name));
      showMessage(`「${name}」を削除しました`);
    } catch (err) {
      showMessage('削除に失敗しました', 'error');
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;

  return (
    <div className="page">
      <h1>種目管理</h1>

      {/* メッセージ */}
      {message.text && (
        <div className={message.type === 'error' ? 'error-message' : 'sync-banner'}>
          {message.type === 'error' ? '⚠️ ' : '✅ '}{message.text}
        </div>
      )}

      {/* 部位タブ */}
      <div className="manage-tabs">
        {bodyParts.map((part) => (
          <button
            key={part.name}
            className={`manage-tab ${selectedBodyPart === part.name ? 'active' : ''}`}
            onClick={() => { setSelectedBodyPart(part.name); cancelEdit(); }}
          >
            {part.name}
          </button>
        ))}
      </div>

      {/* 追加フォーム */}
      <div className="input-form">
        <h2>種目を追加</h2>
        <div className="warn-box">
            ⚠️ 種目名の変更は過去の記録との紐づきが失われます。<br />
            種目名を変更したい場合は「新規追加」で行うことを推奨します。
        </div>
        <div className="input-row">
          <label>種目名</label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`例：インクラインカール`}
            style={{ width: '60%', textAlign: 'left' }}
          />
        </div>
        <div className="input-row">
          <label>表示順</label>
          <input
            type="number"
            value={newOrder}
            onChange={(e) => setNewOrder(e.target.value)}
            placeholder="例：5"
            style={{ width: '60%' }}
          />
        </div>
        <button className="primary-button" onClick={handleCreate}>追加</button>
      </div>

      {/* 種目一覧 */}
      <div className="sets-list">
        <h2>{selectedBodyPart}の種目一覧</h2>
        {exercises.length === 0 ? (
          <p>種目がありません</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>表示順</th>
                <th>種目名</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {exercises.map((exercise) => (
                <tr key={exercise.name}>
                  {editTarget === exercise.name ? (
                    <>
                      <td>
                        <input
                          type="number"
                          value={editOrder}
                          onChange={(e) => setEditOrder(e.target.value)}
                          style={{ width: '60px', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{ width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        />
                      </td>
                      <td style={{ display: 'flex', gap: '4px' }}>
                        <button className="primary-button" style={{ padding: '4px 10px', marginTop: 0, width: 'auto', fontSize: '13px' }} onClick={() => handleUpdate(exercise.name)}>保存</button>
                        <button className="delete-button" onClick={cancelEdit}>取消</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{exercise.displayOrder}</td>
                      <td>{exercise.name}</td>
                      <td style={{ display: 'flex', gap: '4px' }}>
                        <button className="edit-button" onClick={() => startEdit(exercise)}>編集</button>
                        <button className="delete-button" onClick={() => handleDelete(exercise.name)}>削除</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExerciseManagePage;